import {AfterViewInit, Component, ElementRef, HostListener} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {of, Subject, Subscription} from 'rxjs';
import {debounceTime, filter, switchMap} from 'rxjs/operators';
import {TranslateModule} from '@ngx-translate/core';
import {GraphActionsService} from '../../../diagram-interaction/actions/graph-actions.service';
import {fullscreenApiKeys} from '../util/fullscreen';
import {IconButtonComponent, ToolbarModule} from '@q9elements/ui-kit/components';
import {Q9TrackDirective} from '@q9elements/ui-kit/common';

declare const mxClient;
declare const mxUtils;

@Component({
  selector: 'app-bottom-toolbar',
  standalone: true,
  imports: [
    CommonModule,
    ToolbarModule,
    IconButtonComponent,
    MatTooltipModule,
    Q9TrackDirective,
    TranslateModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './bottom-toolbar.component.html',
  styleUrls: ['./bottom-toolbar.component.scss']
})
export class BottomToolbarComponent implements AfterViewInit {
  isFullScreen = false;
  isIE = mxClient.IS_IE || mxClient.IS_IE11;
  public showFullScreen = fullscreenApiKeys.fullscreenEnabled;
  public showBtn = false;
  private subscriptions: Subscription = new Subscription();
  private waitingForEsc = false;
  private savedDimensions = {
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight
  };
  private updateSubject = new Subject();
  private updateSubject$ = this.subscriptions.add(
    this.updateSubject
      .asObservable()
      .pipe(
        filter(Boolean),
        debounceTime(100),
        switchMap(() => {
          this.triggerResize();

          return of(null);
        })
      )
      .subscribe()
  );

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (!event) {
      return;
    }

    const {
      target: {innerWidth, innerHeight}
    } = event;
    const {innerWidth: savedWidth, innerHeight: savedHeight} = this.savedDimensions;
    const gapWidth = Math.abs(innerWidth - savedWidth);
    const gapHeight = Math.abs(innerHeight - savedHeight);

    if (gapHeight + gapWidth > 10) {
      this.savedDimensions = {
        innerWidth,
        innerHeight
      } as any;
      this.updateSubject.next(true);
    }
  }

  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  @HostListener('document:mozfullscreenchange')
  @HostListener('document:MSFullscreenChange')
  screenChange() {
    if (!this.isIE) {
      this.isFullScreen = document[fullscreenApiKeys.isFullScreen];

      return;
    }

    if (this.isFullScreen && this.waitingForEsc) {
      this.isFullScreen = false;
      this.waitingForEsc = false;
    }

    if (this.isFullScreen) {
      this.waitingForEsc = true;
    }
  }

  constructor(
    private graphActionsSvc: GraphActionsService,
    private elementRef: ElementRef
  ) {}

  ngAfterViewInit(): void {
    mxUtils.writeElementBounds('BOTTOM_TOOLBAR_BOUNDS', this.elementRef);
  }

  mouseOver() {
    this.showBtn = !this.showBtn;
  }

  mouseOut() {
    this.showBtn = !this.showBtn;
  }

  zoomIn() {
    this.graphActionsSvc.zoomIn();
  }

  zoomOut() {
    this.graphActionsSvc.zoomOut();
  }

  fit() {
    this.graphActionsSvc.fit();
  }

  fullScreen() {
    const doc = document;
    const isFullScreen = doc[fullscreenApiKeys.isFullScreen];
    const exitFullscreenPolyfill = fullscreenApiKeys.exitFullscreen;

    if (isFullScreen || (this.isFullScreen && this.isIE)) {
      if (doc[exitFullscreenPolyfill]) {
        doc[exitFullscreenPolyfill]();
        this.isFullScreen = false;
        this.waitingForEsc = false;
      }
    } else {
      const docEl = document.documentElement;
      const requestFullscreenPolyfill = fullscreenApiKeys.requestFullscreen;

      if (docEl[requestFullscreenPolyfill]) {
        docEl[requestFullscreenPolyfill]();
        this.isFullScreen = true;
      }
    }
  }

  triggerResize() {
    setTimeout(() => {
      if (typeof Event === 'function') {
        // modern browsers
        window.dispatchEvent(new Event('resize'));
      } else {
        // for IE and other old browsers
        // causes deprecation warning on modern browsers
        const evt = window.document.createEvent('UIEvents');

        evt.initEvent('resize', true, false);
        window.dispatchEvent(evt);
      }
    }, 500);
  }

  get canvasIsEmpty() {
    return !this.graphActionsSvc.canvasNotEmpty;
  }
}
